import React, { useEffect, useState } from "react"
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@material-ui/core"
import ClearIcon from '@material-ui/icons/Clear'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import formStyles from '../TeamsManagement/styles.module.css'
import styles from './styles.module.css'
import { BlueBtn, BlueInverseBtn } from "../Styled/StyledButtons"
import { BlockedNumber } from "../../models/RobocallBlocks/BlockedNumber"

interface IProps {
    show: boolean
    target: BlockedNumber | null
    toggleHandle: () => void
    onSubmit: (comment: string, days?: number, permanent?: boolean) => void
}

/**
 * Extend or shorten an active block.
 *
 * Days are measured from NOW, not added to the current expiry, so repeating the same
 * action is idempotent instead of compounding. Zero is allowed and is the way to end a
 * block "now, but on the record" — use Unblock instead when the number is a false
 * positive, because that also veto-protects it.
 */
const ExpiryDialog = (props: IProps) => {
    const [comment, setComment] = useState('')
    const [days, setDays] = useState<number>(7)
    const [permanent, setPermanent] = useState(false)

    useEffect(() => {
        if (props.show) {
            setComment('')
            setDays(7)
            setPermanent(false)
        }
    }, [props.show])

    const valid = comment.trim().length > 3

    const submit = () => {
        if (!valid) {
            return
        }
        props.onSubmit(comment.trim(), permanent ? undefined : days, permanent)
        props.toggleHandle()
    }

    return (
        <Dialog open={props.show} onClose={props.toggleHandle} maxWidth="sm" fullWidth={true}>
            <DialogTitle>
                {`Change expiry for ${props.target?.phone_e164 || ''}`}
                <IconButton className={mainStyles.floatRight} onClick={props.toggleHandle}>
                    <ClearIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={formStyles.popup}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <div className={styles.Evidence}>
                            Currently expires: <b>{props.target?.expires_at || 'never'}</b>
                        </div>
                        <div className={styles.Hint}>
                            Measured from now, so applying the same value twice changes nothing.
                            Set 0 days to end the block immediately while keeping the record.
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className={formStyles.FormLabel}>Days from now</label>
                        <input
                            type="number"
                            min={0}
                            max={365}
                            disabled={permanent}
                            className={formStyles.FormControl}
                            value={permanent ? '' : days}
                            onChange={(event) => {
                                const parsed = parseInt(event.target.value, 10)
                                setDays(isNaN(parsed) ? 0 : parsed)
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className={formStyles.FormLabel}>&nbsp;</label>
                        <label className={styles.Evidence}>
                            <input
                                type="checkbox"
                                checked={permanent}
                                onChange={(event) => setPermanent(event.target.checked)}
                            />
                            &nbsp;Permanent
                        </label>
                    </Grid>
                    <Grid item xs={12}>
                        <label className={formStyles.FormLabel}>Comment (required)</label>
                        <input
                            placeholder="Why?"
                            className={`${(!valid && comment !== '') ? formStyles.FormInvalid : ""} ${formStyles.FormControl}`}
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <BlueInverseBtn fullWidth={true} onClick={props.toggleHandle}>Cancel</BlueInverseBtn>
                    </Grid>
                    <Grid item xs={6}>
                        <BlueBtn fullWidth={true} disabled={!valid} onClick={submit}>Apply</BlueBtn>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

export default ExpiryDialog
